import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { CheckCircle, ArrowLeft, ArrowRight } from 'lucide-react';
import { AZURE } from '../services/api';
import { resolveLocale, saveLocale } from '../utils/vukzLocale';

/**
 * RequestAccessPage — solicitação de acesso ("Solicitar acesso" / "Request access").
 * Cria APENAS um lead comercial via POST /public/access-request.
 * Não cria usuário, tenant, assinatura, entitlement, role ou dados demo.
 *
 * v2 (P1.1 + P1.2):
 * - Header no padrão visual VUKZ (logo, navbar, seletor PT | EN).
 * - Locale: rota → vukz_locale salvo → navegador → fallback. Persiste em localStorage.
 * - Troca de idioma NÃO perde o estado do formulário.
 * - Geo canônico: país (BR/US/AU) → estado → cidade (pesquisável), via /public/geo/*.
 */
const V = {
  bg: '#ffffff', text: '#111111', textSoft: '#555555', textMute: '#8a8a8a',
  accent: '#111111', border: '#e6e6e6', cardBg: '#fafafa',
};

const T = {
  pt: {
    title: 'Solicite acesso à VUKZ',
    subtitle: 'Conte um pouco sobre você e sua organização. Nossa equipe analisará as informações e entrará em contato.',
    sectionContact: 'Seus dados',
    sectionOrg: 'Organização',
    contactName: 'Nome completo',
    contactRole: 'Cargo ou função',
    // P3.2: apenas Gestor/Treinador podem solicitar acesso — são os papéis
    // que assumem a responsabilidade pelo clube no provisionamento.
    roleOptions: [['', 'Selecione'], ['gestor', 'Gestor / Diretor'], ['treinador', 'Treinador']],
    email: 'E-mail profissional',
    phone: 'Telefone / WhatsApp',
    clubName: 'Nome da organização',
    orgType: 'Tipo de organização',
    orgTypeOptions: [['', 'Selecione'], ['club', 'Clube'], ['academy', 'Academia / Centro de treinamento'], ['school', 'Escola'], ['university', 'Universidade'], ['federation', 'Federação'], ['team', 'Equipe'], ['other', 'Outro']],
    country: 'País',
    state: 'Estado',
    city: 'Cidade',
    citySearch: 'Digite para buscar...',
    selectFirstCountry: 'Selecione o país primeiro',
    selectFirstState: 'Selecione o estado primeiro',
    select: 'Selecione',
    sport: 'Modalidade',
    // P3.2: comercialmente só Natação por enquanto. O contrato do backend
    // segue multi-esporte; apenas o formulário público restringe a opção.
    sportOptions: [['natacao', 'Natação']],
    plan: 'Plano',
    planOptions: [['', 'Selecione'], ['basic', 'Basic — treinos'], ['basic_plus', 'Basic+ — treinos + físico'], ['pro', 'Pro — gestão completa do clube'], ['elite', 'Elite — tudo incluído (nutrição, multiesporte)']],
    planSize: 'Capacidade do plano',
    planSizeUnit: (ath, staff) => `até ${ath} atletas · ${staff} ${staff === 1 ? 'profissional' : 'profissionais'}`,
    selectFirstPlan: 'Selecione o plano primeiro',
    planHint: 'A capacidade define os limites de atletas e profissionais. O valor mensal é o do catálogo vigente; nossa equipe confirma tudo antes da ativação.',
    athletes: 'Número de atletas',
    staffCount: 'Número de profissionais (staff)',
    selectPlanFirstShort: 'Escolha o plano e a capacidade primeiro',
    website: 'Site (opcional)',
    instagram: 'Instagram (opcional)',
    mainNeed: 'Principal necessidade (opcional)',
    mainNeedPlaceholder: 'Ex.: organizar treinos por categoria, acompanhar carga, nutrição do elenco...',
    referral: 'Como conheceu a VUKZ? (opcional)',
    notes: 'Observações (opcional)',
    consent: 'Autorizo o uso destes dados para contato comercial, conforme a Política de Privacidade.',
    submit: 'Enviar solicitação',
    submitting: 'Enviando...',
    requiredError: 'Preencha os campos obrigatórios e aceite a política de privacidade.',
    serverError: 'Não foi possível enviar agora. Tente novamente em instantes.',
    successTitle: 'Solicitação recebida',
    successMsg: 'Recebemos sua solicitação de acesso. Nossa equipe analisará as informações e entrará em contato pelo e-mail informado.',
    successNote: 'Nenhum acesso será criado antes da validação e da definição da configuração da sua organização.',
    backHome: 'Voltar para a homepage',
    signIn: 'Entrar',
    homeLink: '/pt',
  },
  en: {
    title: 'Request access to VUKZ',
    subtitle: 'Tell us a little about you and your organization. Our team will review the information and get in touch.',
    sectionContact: 'Your details',
    sectionOrg: 'Organization',
    contactName: 'Full name',
    contactRole: 'Role or position',
    roleOptions: [['', 'Select'], ['gestor', 'Manager / Director'], ['treinador', 'Coach']],
    email: 'Work email',
    phone: 'Phone / WhatsApp',
    clubName: 'Organization name',
    orgType: 'Organization type',
    orgTypeOptions: [['', 'Select'], ['club', 'Club'], ['academy', 'Academy / Training center'], ['school', 'School'], ['university', 'University'], ['federation', 'Federation'], ['team', 'Team'], ['other', 'Other']],
    country: 'Country',
    state: 'State',
    city: 'City',
    citySearch: 'Type to search...',
    selectFirstCountry: 'Select the country first',
    selectFirstState: 'Select the state first',
    select: 'Select',
    sport: 'Sport',
    // P3.2: commercially Swimming-only for now. Backend contract stays
    // multi-sport; only the public form narrows the choice.
    sportOptions: [['natacao', 'Swimming']],
    plan: 'Plan',
    planOptions: [['', 'Select'], ['basic', 'Basic — training'], ['basic_plus', 'Basic+ — training + physical'], ['pro', 'Pro — full club management'], ['elite', 'Elite — everything included (nutrition, multi-sport)']],
    planSize: 'Plan capacity',
    planSizeUnit: (ath, staff) => `up to ${ath} athletes · ${staff} ${staff === 1 ? 'professional' : 'professionals'}`,
    selectFirstPlan: 'Select the plan first',
    planHint: 'Capacity defines athlete and professional limits. Monthly price follows the current catalog; our team confirms everything before activation.',
    athletes: 'Number of athletes',
    staffCount: 'Number of professionals (staff)',
    selectPlanFirstShort: 'Choose plan and capacity first',
    website: 'Website (optional)',
    instagram: 'Instagram (optional)',
    mainNeed: 'Main need (optional)',
    mainNeedPlaceholder: 'E.g.: organize training by category, track load, squad nutrition...',
    referral: 'How did you hear about VUKZ? (optional)',
    notes: 'Notes (optional)',
    consent: 'I authorize the use of this data for commercial contact, according to the Privacy Policy.',
    submit: 'Send request',
    submitting: 'Sending...',
    requiredError: 'Fill in the required fields and accept the privacy policy.',
    serverError: 'We could not send it right now. Please try again shortly.',
    successTitle: 'Request received',
    successMsg: 'We received your access request. Our team will review the information and contact you at the email provided.',
    successNote: 'No access will be created before validation and the definition of your organization\u2019s setup.',
    backHome: 'Back to homepage',
    signIn: 'Sign in',
    homeLink: '/',
  },
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-sm font-semibold mb-1.5" style={{ color: V.text }}>
      {label}{required && <span aria-hidden="true" style={{ color: '#b91c1c' }}> *</span>}
    </span>
    {children}
  </label>
);

const inputCls = 'w-full px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-gray-900';
const inputStyle = { borderColor: V.border, color: V.text, backgroundColor: '#ffffff' };
const disabledStyle = { ...inputStyle, backgroundColor: V.cardBg, color: V.textMute };

/**
 * P3.2 — Limites por plano (espelho de plan_definitions; autoridade é o
 * backend, que valida o plan_code contra o catálogo no provisionamento).
 * Chave: `${familia}_${porte}` → { ath, staff }.
 */
const PLAN_LIMITS = {
  basic_s: { ath: 10, staff: 1, price: 39000 }, basic_m: { ath: 25, staff: 2, price: 119000 },
  basic_l: { ath: 50, staff: 3, price: 199000 }, basic_xl: { ath: 100, staff: 5, price: 349000 },
  basic_plus_s: { ath: 5, staff: 2, price: 49000 }, basic_plus_m: { ath: 25, staff: 3, price: 149000 },
  basic_plus_l: { ath: 50, staff: 5, price: 249000 }, basic_plus_xl: { ath: 100, staff: 8, price: 449000 },
  pro_s: { ath: 5, staff: 3, price: 69000 }, pro_m: { ath: 25, staff: 4, price: 189000 },
  pro_l: { ath: 50, staff: 6, price: 329000 }, pro_xl: { ath: 100, staff: 10, price: 599000 },
  elite_s: { ath: 10, staff: 4, price: 99000 }, elite_m: { ath: 25, staff: 5, price: 239000 },
  elite_l: { ath: 50, staff: 8, price: 449000 }, elite_xl: { ath: 100, staff: 13, price: 799000 },
};
/** cents → "R$ 390/mês" | "R$390/mo" (preços em BRL no catálogo). */
const fmtPrice = (cents, locale) => {
  const v = (cents / 100).toLocaleString(locale === 'pt' ? 'pt-BR' : 'en-US', { maximumFractionDigits: 0 });
  return locale === 'pt' ? `R$ ${v}/mês` : `R$ ${v}/mo`;
};
const PLAN_SIZES = ['s', 'm', 'l', 'xl'];
const PLAN_FAMILY_NAMES = { basic: 'Basic', basic_plus: 'Basic+', pro: 'Pro', elite: 'Elite' };

/* ── Header VUKZ (mesmo padrão visual das homepages) ── */
const VukzHeader = ({ locale, onLocale, t }) => (
  <nav
    className="fixed top-0 inset-x-0 z-50 bg-white/80 backdrop-blur-md border-b"
    style={{ borderColor: V.border }}
    role="navigation"
    aria-label="VUKZ"
  >
    <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
      <Link to={locale === 'pt' ? '/pt' : '/'} className="flex items-center gap-3 flex-shrink-0" aria-label="VUKZ">
        <img src="/vukz-logo.jpeg" alt="VUKZ Performance Intelligence Platform" className="h-10 w-auto" />
      </Link>
      <div className="flex items-center gap-3">
        <div className="flex items-center rounded-lg border overflow-hidden" style={{ borderColor: V.border }} aria-label="Language">
          {['en', 'pt'].map((l) => (
            locale === l ? (
              <span key={l} className="px-3 py-1.5 text-xs font-bold" style={{ backgroundColor: V.accent, color: '#fff' }} aria-current="true">
                {l.toUpperCase()}
              </span>
            ) : (
              <button key={l} type="button" onClick={() => onLocale(l)}
                className="px-3 py-1.5 text-xs font-semibold hover:bg-gray-100 transition-colors"
                style={{ color: V.textSoft }}>
                {l.toUpperCase()}
              </button>
            )
          ))}
        </div>
        <Link to="/login" className="hidden sm:inline-block text-sm font-semibold px-3 py-2 rounded-lg transition-opacity hover:opacity-70" style={{ color: V.text }}>
          {t.signIn}
        </Link>
      </div>
    </div>
  </nav>
);

/* ── Combobox de cidade (pesquisável, sem texto livre) ── */
const CityCombobox = ({ t, disabled, cities, value, onSelect }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClickOut = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClickOut);
    return () => document.removeEventListener('mousedown', onClickOut);
  }, []);

  const norm = (s) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  const filtered = query ? cities.filter((c) => norm(c.name).includes(norm(query))) : cities;

  return (
    <div ref={boxRef} className="relative">
      <input
        className={inputCls}
        style={disabled ? disabledStyle : inputStyle}
        disabled={disabled}
        placeholder={disabled ? t.selectFirstState : t.citySearch}
        value={open ? query : (value?.name || '')}
        onFocus={() => { if (!disabled) { setOpen(true); setQuery(''); } }}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
      />
      {open && !disabled && (
        <ul className="absolute z-20 mt-1 w-full max-h-56 overflow-auto bg-white rounded-xl border shadow-lg" style={{ borderColor: V.border }} role="listbox">
          {filtered.length === 0 && (
            <li className="px-4 py-2.5 text-sm" style={{ color: V.textMute }}>—</li>
          )}
          {filtered.slice(0, 100).map((c) => (
            <li key={c.city_id} role="option" aria-selected={value?.city_id === c.city_id}>
              <button
                type="button"
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50"
                style={{ color: V.text }}
                onClick={() => { onSelect(c); setOpen(false); setQuery(''); }}
              >
                {c.name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const RequestAccessPage = ({ lang }) => {
  const [locale, setLocale] = useState(() => resolveLocale(lang));
  const t = T[locale] || T.pt;
  const location = useLocation();

  useEffect(() => { saveLocale(locale); }, [locale]);

  const utm = useMemo(() => {
    const p = new URLSearchParams(location.search);
    return {
      utm_source: p.get('utm_source') || undefined,
      utm_medium: p.get('utm_medium') || undefined,
      utm_campaign: p.get('utm_campaign') || undefined,
    };
  }, [location.search]);

  const [form, setForm] = useState({
    contact_name: '', contact_role: '', email: '', phone: '',
    club_name: '', organization_type: '', sport: 'natacao', athletes_range: '', staff_range: '',
    plan_family: '', plan_size: '',
    main_need: '', website: '', instagram: '', referral_source: '', notes: '',
    privacy_consent: false, company_website_hp: '',
  });

  // Geo canônico
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [country, setCountry] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [city, setCity] = useState(null); // {city_id, name}

  useEffect(() => {
    AZURE.get('/public/geo/countries')
      .then(({ data }) => setCountries(data.data || []))
      .catch(() => setCountries([]));
  }, []);

  useEffect(() => {
    setStates([]); setStateCode(''); setCities([]); setCity(null);
    if (!country) return;
    AZURE.get('/public/geo/states', { params: { country } })
      .then(({ data }) => setStates(data.data || []))
      .catch(() => setStates([]));
  }, [country]);

  useEffect(() => {
    setCities([]); setCity(null);
    if (!country || !stateCode) return;
    AZURE.get('/public/geo/cities', { params: { country, state: stateCode } })
      .then(({ data }) => setCities(data.data || []))
      .catch(() => setCities([]));
  }, [country, stateCode]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    if (!form.contact_name.trim() || !form.email.trim() || !form.club_name.trim()
      || !country || !stateCode || !city
      || !['gestor', 'treinador'].includes(form.contact_role)
      || !form.plan_family || !form.plan_size
      || !form.athletes_range || !form.staff_range
      || !form.privacy_consent) {
      setError(t.requiredError);
      return;
    }
    setSubmitting(true);
    // plan_code OBRIGATÓRIO no fluxo público: família + porte escolhidos
    // pelo cliente. Códigos válidos de plan_definitions (basic_s..elite_xl).
    // Venda consultiva sem plano é exclusividade do Ops (override).
    const planCode = `${form.plan_family}_${form.plan_size}`;
    const { plan_family, plan_size, ...payload } = form;
    try {
      await AZURE.post('/public/access-request', {
        ...payload,
        plan_code: planCode,
        country_code: country,
        state_code: stateCode,
        city_id: city.city_id,
        // city/state NÃO são enviados: o backend deriva os snapshots
        // do catálogo canônico (autoridade no servidor).
        language: locale,
        ...utm,
      });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.error || t.serverError);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen" style={{ backgroundColor: V.bg }}>
        <VukzHeader locale={locale} onLocale={setLocale} t={t} />
        <div className="min-h-screen flex items-center justify-center px-4 pt-16">
          <div className="max-w-xl w-full bg-white rounded-2xl border p-8 sm:p-10 text-center" style={{ borderColor: V.border }}>
            <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: '#dcfce7' }}>
              <CheckCircle size={32} style={{ color: '#16a34a' }} aria-hidden="true" />
            </div>
            <h1 className="text-3xl font-black tracking-tight" style={{ color: V.text }}>{t.successTitle}</h1>
            <p className="mt-4 text-base leading-relaxed" style={{ color: V.textSoft }}>{t.successMsg}</p>
            <p className="mt-3 text-sm" style={{ color: '#9ca3af' }}>{t.successNote}</p>
            <div className="mt-8 flex justify-center">
              <Link to={t.homeLink} className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold text-sm text-white" style={{ backgroundColor: V.text }}>
                <ArrowLeft size={16} aria-hidden="true" /> {t.backHome}
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: V.bg }}>
      <VukzHeader locale={locale} onLocale={setLocale} t={t} />

      <main className="pt-16 pb-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <header className="pt-12 pb-8">
            <h1 className="text-4xl font-black tracking-tight" style={{ color: V.text }}>{t.title}</h1>
            <p className="mt-3 text-base leading-relaxed" style={{ color: V.textSoft }}>{t.subtitle}</p>
          </header>

          <form onSubmit={onSubmit} noValidate>
            {/* Honeypot anti-bot (invisível). BUG FIX 25/08: o Chrome
                autofill preenchia o campo (nome continha "website") e o
                backend descartava o lead silenciosamente. Agora: nome
                aleatório sem semântica, autocomplete off por atributo
                não-padrão, readOnly até foco (autofill não preenche
                readOnly), tabIndex -1. */}
            <input
              type="text" name="xq_field_9z" value={form.company_website_hp}
              onChange={set('company_website_hp')} tabIndex={-1}
              autoComplete="one-time-code" readOnly
              onFocus={(e) => e.target.removeAttribute('readonly')}
              style={{ position: 'absolute', left: '-9999px', width: 1, height: 1 }} aria-hidden="true"
            />

            {/* ── Seus dados ── */}
            <section className="bg-white rounded-2xl border p-6 sm:p-8 mb-6" style={{ borderColor: V.border }}>
              <h2 className="text-lg font-extrabold mb-5" style={{ color: V.text }}>{t.sectionContact}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label={t.contactName} required>
                  <input className={inputCls} style={inputStyle} value={form.contact_name} onChange={set('contact_name')} required autoComplete="name" />
                </Field>
                <Field label={t.contactRole} required>
                  <select className={inputCls} style={inputStyle} value={form.contact_role} onChange={set('contact_role')}>
                    {t.roleOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </Field>
                <Field label={t.email} required>
                  <input type="email" className={inputCls} style={inputStyle} value={form.email} onChange={set('email')} required autoComplete="email" />
                </Field>
                <Field label={t.phone} required>
                  <input type="tel" className={inputCls} style={inputStyle} value={form.phone} onChange={set('phone')} autoComplete="tel" />
                </Field>
              </div>
            </section>

            {/* ── Organização ── */}
            <section className="bg-white rounded-2xl border p-6 sm:p-8" style={{ borderColor: V.border }}>
              <h2 className="text-lg font-extrabold mb-5" style={{ color: V.text }}>{t.sectionOrg}</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="sm:col-span-2">
                  <Field label={t.clubName} required>
                    <input className={inputCls} style={inputStyle} value={form.club_name} onChange={set('club_name')} required autoComplete="organization" />
                  </Field>
                </div>
                <Field label={t.orgType} required>
                  <select className={inputCls} style={inputStyle} value={form.organization_type} onChange={set('organization_type')}>
                    {t.orgTypeOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </Field>
                <Field label={t.country} required>
                  <select className={inputCls} style={inputStyle} value={country} onChange={(e) => setCountry(e.target.value)}>
                    <option value="">{t.select}</option>
                    {countries.map((c) => (
                      <option key={c.country_code} value={c.country_code}>
                        {locale === 'pt' ? c.name_pt : c.name_en}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label={t.state} required>
                  <select
                    className={inputCls}
                    style={country ? inputStyle : disabledStyle}
                    disabled={!country}
                    value={stateCode}
                    onChange={(e) => setStateCode(e.target.value)}
                  >
                    <option value="">{country ? t.select : t.selectFirstCountry}</option>
                    {states.map((s) => <option key={s.state_code} value={s.state_code}>{s.name}</option>)}
                  </select>
                </Field>
                <Field label={t.city} required>
                  <CityCombobox t={t} disabled={!stateCode} cities={cities} value={city} onSelect={setCity} />
                </Field>
                <Field label={t.sport} required>
                  <select className={inputCls} style={inputStyle} value={form.sport} onChange={set('sport')}>
                    {t.sportOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </Field>
                <div className="sm:col-span-2">
                  <Field label={t.plan} required>
                    <select
                      className={inputCls} style={inputStyle} value={form.plan_family} required
                      onChange={(e) => setForm((f) => ({ ...f, plan_family: e.target.value, plan_size: '', athletes_range: '', staff_range: '' }))}
                    >
                      {t.planOptions.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label={t.planSize} required>
                    <select
                      className={inputCls}
                      style={form.plan_family ? inputStyle : disabledStyle}
                      disabled={!form.plan_family}
                      value={form.plan_size}
                      onChange={(e) => setForm((f) => ({ ...f, plan_size: e.target.value, athletes_range: '', staff_range: '' }))}
                      required
                    >
                      <option value="">{form.plan_family ? t.select : t.selectFirstPlan}</option>
                      {form.plan_family && PLAN_SIZES.map((s) => {
                        const lim = PLAN_LIMITS[`${form.plan_family}_${s}`];
                        if (!lim) return null;
                        return (
                          <option key={s} value={s}>
                            {PLAN_FAMILY_NAMES[form.plan_family]} {s.toUpperCase()} — {t.planSizeUnit(lim.ath, lim.staff)} · {fmtPrice(lim.price, locale)}
                          </option>
                        );
                      })}
                    </select>
                    <p className="mt-1.5 text-xs" style={{ color: V.textMute }}>{t.planHint}</p>
                  </Field>
                </div>
                {/* Seletores numéricos derivados do plano escolhido (P3.2):
                    o cliente informa o nº exato de atletas/staff dentro da
                    capacidade contratada — substitui as faixas genéricas. */}
                {(() => {
                  const lim = PLAN_LIMITS[`${form.plan_family}_${form.plan_size}`];
                  const range = (n) => Array.from({ length: n }, (_, i) => i + 1);
                  return (
                    <>
                      <Field label={t.athletes} required>
                        <select
                          className={inputCls}
                          style={lim ? inputStyle : disabledStyle}
                          disabled={!lim}
                          value={form.athletes_range}
                          onChange={set('athletes_range')}
                        >
                          <option value="">{lim ? t.select : t.selectPlanFirstShort}</option>
                          {lim && range(lim.ath).map((n) => <option key={n} value={String(n)}>{n}</option>)}
                        </select>
                      </Field>
                      <Field label={t.staffCount} required>
                        <select
                          className={inputCls}
                          style={lim ? inputStyle : disabledStyle}
                          disabled={!lim}
                          value={form.staff_range}
                          onChange={set('staff_range')}
                        >
                          <option value="">{lim ? t.select : t.selectPlanFirstShort}</option>
                          {lim && range(lim.staff).map((n) => <option key={n} value={String(n)}>{n}</option>)}
                        </select>
                      </Field>
                    </>
                  );
                })()}
                <Field label={t.website}>
                  <input className={inputCls} style={inputStyle} value={form.website} onChange={set('website')} />
                </Field>
                <Field label={t.instagram}>
                  <input className={inputCls} style={inputStyle} value={form.instagram} onChange={set('instagram')} />
                </Field>
                <Field label={t.referral}>
                  <input className={inputCls} style={inputStyle} value={form.referral_source} onChange={set('referral_source')} />
                </Field>
                <div className="sm:col-span-2">
                  <Field label={t.mainNeed}>
                    <textarea rows={3} className={inputCls} style={inputStyle} placeholder={t.mainNeedPlaceholder} value={form.main_need} onChange={set('main_need')} />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label={t.notes}>
                    <input className={inputCls} style={inputStyle} value={form.notes} onChange={set('notes')} />
                  </Field>
                </div>
              </div>
            </section>

            <label className="flex items-start gap-3 mt-6 cursor-pointer">
              <input type="checkbox" checked={form.privacy_consent} onChange={set('privacy_consent')} className="mt-1" required />
              <span className="text-sm leading-relaxed" style={{ color: V.textSoft }}>{t.consent}</span>
            </label>

            {error && (
              <div role="alert" className="mt-5 px-4 py-3 rounded-xl text-sm font-semibold" style={{ backgroundColor: '#fef2f2', color: '#b91c1c' }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-8 w-full py-3.5 rounded-xl font-bold text-base text-white disabled:opacity-60 inline-flex items-center justify-center gap-2"
              style={{ backgroundColor: V.text }}
            >
              {submitting ? t.submitting : t.submit}
              {!submitting && <ArrowRight size={16} aria-hidden="true" />}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default RequestAccessPage;
